<script setup lang="ts">
import type { PropType } from "vue";
import type { Product } from "~/types";
import { useProcessStore } from "~/stores/process/store";

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
  highlighted: {
    type: Boolean,
    default: false,
  },
  hideActions: {
    type: Boolean,
    default: false,
  },
  hidePriceDescription: {
    type: Boolean,
    default: false,
  },
  actionLabel: {
    type: String,
    default: "Select Plan",
  },
  actionDisabled: {
    type: Boolean,
    default: false,
  },
  actionLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (e: "handle-action", product: Product): void;
}>();

function handleClickAction(product: Product) {
  emit("handle-action", product);
}

const productHighlighted = computed(() => {
  return props.product.highlighted || props.highlighted;
});
</script>

<template>
  <div
    :class="[
      'relative flex w-full flex-col justify-between overflow-hidden rounded-xl bg-surface-bg px-5 py-8 md:h-[730px] md:w-[18.4rem]',
      { 'bg-white': productHighlighted },
    ]"
  >
    <div v-if="product.ribbon" class="ribbon text-black">
      <span>{{ product.ribbon }}</span>
    </div>
    <div>
      <slot name="header" />
      <div
        :class="[
          'mb-5 text-2xl font-bold',
          { '!text-black': productHighlighted },
        ]"
      >
        {{ product.name }}
      </div>
      <div class="flex flex-col gap-5">
        <div v-for="(detail, idx) in product.planDetails" :key="idx">
          <div
            :class="[
              'mb-4 font-bold text-surface-300',
              { '!text-black': productHighlighted },
            ]"
          >
            {{ detail.title }}
          </div>
          <div class="flex flex-col gap-2">
            <div
              v-for="(item, index) in detail.details"
              :key="index"
              class="flex items-center gap-2"
            >
              <font-awesome-icon
                :icon="item.icon"
                :class="[
                  'text-primary-200',
                  { '!text-primary': productHighlighted },
                ]"
              />
              <div
                :class="[
                  'text-surface-300',
                  { '!text-black': productHighlighted },
                ]"
              >
                {{ item.text }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-5">
      <div class="-mb-3 text-center text-surface-300 line-through">
        {{ product.striked }}
      </div>
      <div class="my-5 flex flex-col justify-center gap-1">
        <div :class="['text-4xl', { 'text-black': productHighlighted }]">
          ${{ product.price }}
        </div>
        <div
          v-if="!hidePriceDescription"
          :class="[
            'flex flex-col justify-end text-xs',
            { 'text-black': productHighlighted },
            { 'text-white': !productHighlighted },
          ]"
        >
          <div v-html="product.priceDescription" />
        </div>
      </div>
      <Button
        v-if="!hideActions"
        :disabled="actionDisabled"
        :loading="actionLoading"
        fluid
        :label="actionLabel"
        @click="handleClickAction(product)"
      />
    </div>
  </div>
</template>

<style scoped>
.ribbon {
  margin: 0;
  background: #309251;
  color: white;
  padding: 4px 0;
  position: absolute;
  top: 0;
  right: 0;
  font-weight: bold;
  font-style: italic;
  font-size: 11px;
  text-transform: uppercase;
  transform: translateX(-40%) translateY(150%) rotate(35deg);
  transform-origin: top right;
}
.ribbon:before,
.ribbon:after {
  content: "";
  position: absolute;
  top: 0;
  margin: 0 -1px;
  width: 100%;
  height: 100%;
  background: #309251;
}
.ribbon:before {
  right: 100%;
}
.ribbon:after {
  left: 100%;
}
</style>
